import { useMemo, useReducer, useRef } from 'react';
import { serializeActor } from 'ui-product-approvals-common/utils/actorKey';
import { reorderChangesLockedStep, stepHasLockedApprover } from '../utils/approvalStepsUtils';
const EMPTY_LOCKED_ACTOR_KEYS = new Set();
const makeStep = (seq, approvers = []) => ({
  id: `step-${seq}`,
  approvers
});
export const initApprovalStepsState = (groups, startSeq = 0) => {
  if (groups.length === 0) {
    return {
      steps: [makeStep(startSeq)],
      seq: startSeq + 1
    };
  }
  return {
    steps: groups.map((approvers, index) => makeStep(startSeq + index, approvers)),
    seq: startSeq + groups.length
  };
};
const moveInArray = (arr, fromIndex, toIndex) => {
  const next = [...arr];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
};
export const approvalStepsReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_STEP':
      {
        const steps = [...state.steps];
        const atIndex = action.atIndex === undefined ? steps.length : Math.min(Math.max(action.atIndex, 0), steps.length);
        steps.splice(atIndex, 0, makeStep(state.seq));
        return {
          steps,
          seq: state.seq + 1
        };
      }
    case 'REMOVE_STEP':
      return Object.assign({}, state, {
        steps: state.steps.filter(step => step.id !== action.stepId)
      });
    case 'REORDER_STEPS':
      return Object.assign({}, state, {
        steps: moveInArray(state.steps, action.fromIndex, action.toIndex)
      });
    case 'ADD_APPROVER':
      return Object.assign({}, state, {
        steps: state.steps.map(step => step.id === action.stepId && !step.approvers.some(approver => serializeActor(approver.actor) === serializeActor(action.approver.actor)) ? Object.assign({}, step, {
          approvers: [...step.approvers, action.approver]
        }) : step)
      });
    case 'REMOVE_APPROVER':
      return Object.assign({}, state, {
        steps: state.steps.map(step => step.id === action.stepId ? Object.assign({}, step, {
          approvers: step.approvers.filter(approver => serializeActor(approver.actor) !== action.actorKey)
        }) : step)
      });
    case 'MOVE_APPROVER':
      {
        let moved;
        const withoutApprover = state.steps.map(step => {
          const found = step.approvers.find(approver => serializeActor(approver.actor) === action.actorKey);
          if (!found) {
            return step;
          }
          moved = found;
          return Object.assign({}, step, {
            approvers: step.approvers.filter(approver => serializeActor(approver.actor) !== action.actorKey)
          });
        });
        if (!moved) {
          return state;
        }
        const movedApprover = moved;
        return Object.assign({}, state, {
          steps: withoutApprover.map(step => {
            if (step.id !== action.toStepId) {
              return step;
            }
            const approvers = [...step.approvers];
            const insertIndex = action.toIndex === undefined ? approvers.length : Math.min(Math.max(action.toIndex, 0), approvers.length);
            approvers.splice(insertIndex, 0, movedApprover);
            return Object.assign({}, step, {
              approvers
            });
          })
        });
      }
    case 'INIT':
      return initApprovalStepsState(action.groups, state.seq);
    default:
      return state;
  }
};
export const useApprovalSteps = (initialGroups = [], lockedActorKeys = EMPTY_LOCKED_ACTOR_KEYS) => {
  const [state, dispatch] = useReducer(approvalStepsReducer, initialGroups, initApprovalStepsState);

  // Already-approved approvers (and the steps containing them) are locked: they
  // can't be removed, moved, or have their step order changed. The latest set
  // and state are read through refs so the guards stay correct as
  // approvedApproverActorKeys loads, while keeping the action creators stable.
  const lockedActorKeysRef = useRef(lockedActorKeys);
  lockedActorKeysRef.current = lockedActorKeys;
  const stateRef = useRef(state);
  stateRef.current = state;
  const actions = useMemo(() => ({
    addStep: atIndex => dispatch({
      type: 'ADD_STEP',
      atIndex
    }),
    removeStep: stepId => {
      const step = stateRef.current.steps.find(s => s.id === stepId);
      if (step && stepHasLockedApprover(step, lockedActorKeysRef.current)) {
        return;
      }
      dispatch({
        type: 'REMOVE_STEP',
        stepId
      });
    },
    reorderSteps: (fromIndex, toIndex) => {
      if (reorderChangesLockedStep(stateRef.current.steps, fromIndex, toIndex, lockedActorKeysRef.current)) {
        return;
      }
      dispatch({
        type: 'REORDER_STEPS',
        fromIndex,
        toIndex
      });
    },
    addApprover: (stepId, approver) => dispatch({
      type: 'ADD_APPROVER',
      stepId,
      approver
    }),
    removeApprover: (stepId, actorKey) => {
      if (lockedActorKeysRef.current.has(actorKey)) {
        return;
      }
      dispatch({
        type: 'REMOVE_APPROVER',
        stepId,
        actorKey
      });
    },
    moveApprover: args => {
      if (lockedActorKeysRef.current.has(args.actorKey)) {
        return;
      }
      dispatch(Object.assign({
        type: 'MOVE_APPROVER'
      }, args));
    },
    init: groups => dispatch({
      type: 'INIT',
      groups
    })
  }), []);
  return Object.assign({
    steps: state.steps
  }, actions);
};